import {
  type ApiOutputs,
  type EarlyAccessFeature,
  knownEarlyAccess,
} from "@rosc/contracts";
import type { VisitRetentionPeriod } from "@/common/types/settingsOptions";
import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";
import { referenceClient } from "@/lib/orpc/client";

export type UserPreference = Omit<
  ApiOutputs["me"]["preferences"],
  "style" | "format" | "retentionPeriod"
>;

export interface PopulatedUserPreference extends Partial<UserPreference> {
  visitRetentionPeriod?: VisitRetentionPeriod;
  transcriptionLanguage?: TranscriptionLanguage;
}

export type UserData = Pick<ApiOutputs["me"], "public_id" | "email" | "phone"> &
  ApiOutputs["me"]["subscription"] &
  Pick<
    ApiOutputs["me"]["preferences"],
    "style" | "format" | "retentionPeriod"
  > & {
    firstname: ApiOutputs["me"]["firstName"];
    lastname: ApiOutputs["me"]["lastName"];
    userPreference: UserPreference;
    // Known early-access keys only; none from an API without the field.
    earlyAccess?: EarlyAccessFeature[];
  };

const mapOwnAccount = ({
  firstName,
  lastName,
  subscription,
  preferences,
  earlyAccess,
  ...profile
}: ApiOutputs["me"]): UserData => {
  const { style, format, retentionPeriod, ...userPreference } = preferences;
  return {
    ...profile,
    firstname: firstName,
    lastname: lastName,
    ...subscription,
    style,
    format,
    retentionPeriod,
    userPreference,
    earlyAccess: knownEarlyAccess(earlyAccess),
  };
};

export const getUser = async (): Promise<{
  error: boolean;
  user: UserData | null;
}> => {
  try {
    return { error: false, user: mapOwnAccount(await referenceClient.me({})) };
  } catch {
    return { error: true, user: null };
  }
};
